/**
 * App Content Component
 *
 * Main application layout component that renders the sidebar, main content area,
 * and various modal dialogs.
 */

import React, { memo, useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import SideBar from './SideBar';
import TopBar from './TopBar';
import PageTransition from './PageTransition';
import GlobalSearchModal from './GlobalSearchModal';
import AccessibilityModal from './AccessibilityModal';
import DevOrganizationSwitcher from './DevOrganizationSwitcher';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

/**
 * AppContent - Main application layout
 *
 * @returns {JSX.Element} Main application layout
 */
const AppContent = memo(() => {
  // Initialize keyboard shortcuts (now inside AccessibilityProvider)
  useKeyboardShortcuts();

  // Ref to access SideBar's toggle function
  const sidebarRef = useRef(null);

  // Handle mobile menu toggle from TopBar
  const handleMobileMenuToggle = useCallback(() => {
    if (sidebarRef.current && sidebarRef.current.toggleSidebar) {
      sidebarRef.current.toggleSidebar();
    }
  }, []);

  return (
    <div className="App">
      <SideBar ref={sidebarRef} />
      <TopBar onMobileMenuToggle={handleMobileMenuToggle} />
      <PageTransition>
        <div className="main-content special">
          <Outlet />
        </div>
      </PageTransition>

      {/* Global Search Modal */}
      <GlobalSearchModal />

      {/* Accessibility Modal */}
      <AccessibilityModal />

      {/* Development Organization Switcher */}
      <DevOrganizationSwitcher />
    </div>
  );
});

AppContent.displayName = 'AppContent';

export default AppContent;